import React, { useState } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { message, Table } from "antd"
import { FiEdit2, FiTrash } from "react-icons/fi"

import {
    createDocument,
    deleteDocument,
    updateDocument,
    uploadFile,
    USER_AUTH_HEADER,
} from "../../../../../api"
import useRestTable from "../../../../_shared/hooks/usePaginatedRestResource"
import { useStateValue } from "../../../../_shared/context/AppStateStore"
import FloatingButton from "../../../../_shared/components/FloatingButton"
import Popconfirm from "../../../../_shared/components/Popconfirm"
import DocumentsBottomSheet from "./DocumentsBottomSheet"
import AppButton from "../../../../_shared/components/AppButton"
import ScreenTitle from "../../../../_shared/components/Typography/ScreenTitle"

// ----------------------------------------------------------------------------

function _Documents(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [{ token }] = useStateValue()

    const [documents, fetchDocuments, isLoading, pagination, total] =
        useRestTable("/documents", USER_AUTH_HEADER)

    const [selectedDocument, setSelectedDocument] = useState(null)
    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component functions
    // -------------------------------------

    async function save(values) {
        try {
            const { url } = values.url
                ? typeof values.url === "string"
                    ? { url: values.url }
                    : await uploadFile(values.url, token)
                : { url: "" }
            const body = { ...values, url: url }
            values.id
                ? await updateDocument(values.id, body, token)
                : await createDocument(body, token)
            message.success("Evento creato.")
            fetchDocuments()
            closeSheet()
        } catch (e) {
            message.error(`Non siamo riusciti a creare l'evento: ${e}`)
        }
    }

    function closeSheet() {
        setSelectedDocument(null)
    }

    function openEventForm(event) {
        return () => setSelectedDocument({ ...event })
    }

    async function handleDeleteEvent(row) {
        await deleteDocument(row.id, token)
        message.success("Evento eliminato")
        fetchDocuments()
    }

    function renderRowActions(row) {
        return (
            <div className="row-actions">
                <FloatingButton onClick={openEventForm(row)}>
                    <FiEdit2 className="icn" />
                </FloatingButton>
                <Popconfirm
                    placement="left"
                    title="Sei sicuro di voler eliminare questo catalogo?"
                    okText="Sì"
                    cancelText="No"
                    onConfirm={() => {
                        handleDeleteEvent(row)
                    }}
                >
                    <FloatingButton>
                        <FiTrash className="icn" />
                    </FloatingButton>
                </Popconfirm>
            </div>
        )
    }

    function handleTableChange(pagination) {
        fetchDocuments({ pagination })
    }

    function getPaginationObject() {
        return {
            size: "small",
            total: total,
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: false,
        }
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={props.className}>
            <ScreenTitle text="Documenti"></ScreenTitle>
            <div className="top-bar">
                <AppButton
                    onClick={openEventForm({})}
                    uxType="primary"
                    htmlType="button"
                >
                    Crea nuovo documento
                </AppButton>
            </div>
            <div className="container">
                <Table
                    dataSource={documents}
                    rowKey={(row) => row.id}
                    columns={getColumns(renderRowActions)}
                    loading={isLoading}
                    pagination={getPaginationObject()}
                    onChange={handleTableChange}
                    checkStrictly={true}
                />
            </div>
            <DocumentsBottomSheet
                open={selectedDocument ? true : false}
                document={selectedDocument}
                onConfirm={save}
                onCancel={closeSheet}
            />
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_Documents.propTypes = {
    className: PropTypes.string.isRequired,
}

_Documents.defaultProps = {}

// ----------------------------------------------------------------------------

const Documents = styled(_Documents)`
    & {
        h1 {
            text-align: left;
        }
        .top-bar {
            margin: 20px 0;

            display: flex;
            flex-direction: row;
            align-items: center;
        }

        .ant-table-cell {
            .row-actions {
                display: flex;
                justify-content: space-between;
            }
        }

        .table {
            tr {
                .row-actions {
                    display: flex;
                    justify-content: space-between;
                    transition: all 0.2s ease-in-out;
                    opacity: 0;
                }

                :hover {
                    .row-actions {
                        opacity: 1;
                    }
                }
            }
        }

        .icn {
            font-size: 20px;
            color: ${(props) => props?.theme?.colors?.textPrimary};
        }
    }
`
// ----------------------------------------------------------------------------

export default Documents

export function getColumns(renderRowActions) {
    return [
        { title: "Titolo", dataIndex: "title", key: "title" },
        { title: "URL", dataIndex: "url", key: "URL" },
        {
            render: renderRowActions,
            title: "",
            dataIndex: "",
            key: "range-actions",
        },
    ]
}
